import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Stack, Text, TextInput } from "@mantine/core";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faUser, faInfoCircle, faSpinner, faSearch, faUsers, faUserCircle, faPlus } from '@fortawesome/free-solid-svg-icons';
import PersonApi from "../../../data/api/personsApi";
import { IPerson } from "../../../types/persons/person";

interface PersonMultiSelectProps {
  label: string;
  value: string[];  // Array of person IDs
  onChange: (personIds: string[]) => void;
  associationId: string;
  placeholder?: string;
  helpText?: string;
  disabled?: boolean;
}

const PersonMultiSelect: React.FunctionComponent<PersonMultiSelectProps> = ({
  label,
  value,
  onChange,
  associationId,
  placeholder = "Start typing to search for people...",
  helpText,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [allPersons, setAllPersons] = useState<IPerson[]>([]);
  const [filteredPersons, setFilteredPersons] = useState<IPerson[]>([]);
  const [selectedPersons, setSelectedPersons] = useState<IPerson[]>([]);
  const [loading, setLoading] = useState(false);

  // Load all persons for this association
  useEffect(() => {
    if (associationId) {
      setLoading(true);
      PersonApi.getActiveAtAssociation(associationId)
        .then(persons => {
          setAllPersons(persons);
        })
        .catch(error => {
          console.error("Failed to load persons:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [associationId]);

  // Load selected persons details
  useEffect(() => {
    if (value && value.length > 0) {
      PersonApi.getPersonsByIds(value)
        .then(persons => {
          setSelectedPersons(persons);
        })
        .catch(error => {
          console.error("Failed to load selected persons:", error);
        });
    } else {
      setSelectedPersons([]);
    }
  }, [value]);

  // Filter persons based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredPersons([]);
      return;
    }

    const filtered = allPersons
      .filter(person => 
        !value.includes(person._id) && // Don't show already selected persons
        (person.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
         person.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
         person.lastName.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      .slice(0, 10); // Limit to first 10 results

    setFilteredPersons(filtered);
  }, [searchTerm, allPersons, value]);

  const handlePersonSelect = useCallback((person: IPerson) => {
    const newValue = [...value, person._id];
    onChange(newValue);
    setSearchTerm("");
    setFilteredPersons([]);
  }, [value, onChange]);

  const handlePersonRemove = useCallback((personId: string) => {
    const newValue = value.filter(id => id !== personId);
    onChange(newValue);
  }, [value, onChange]);

  return (
    <Stack gap="sm">
      <Text size="sm" fw={500} className={disabled ? 'text-muted' : ''}>
        {label}
        {disabled && <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" />}
      </Text>
      {helpText && <div className="text-muted small mb-2">{helpText}</div>}
      
      {/* Selected persons */}
      {selectedPersons.length > 0 && (
        <div className="mb-3">
          <div className="d-flex flex-wrap gap-2">
            {selectedPersons.map(person => (
              <Badge 
                key={person._id} 
                color="blue" 
                className="d-inline-flex align-items-center rounded-pill"
                style={{ fontSize: '0.85em', padding: '0.5rem 0.75rem' }}
              >
                <FontAwesomeIcon icon={faUser} className="me-2" style={{ fontSize: '0.8em' }} />
                {person.fullName}
                {!disabled && (
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: '0.6em' }}
                    onClick={() => handlePersonRemove(person._id)}
                    aria-label={`Remove ${person.fullName}`}
                  />
                )}
              </Badge>
            ))}
          </div>
          <div className="small text-muted mt-1">
            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
            {selectedPersons.length} person{selectedPersons.length !== 1 ? 's' : ''} selected
          </div>
        </div>
      )}

      {/* Search input */}
      {!disabled && (
        <div className="position-relative">
          <TextInput
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={loading}
            className="pe-5"
          />
          <div className="position-absolute top-50 end-0 translate-middle-y me-3">
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin className="text-muted" />
            ) : searchTerm ? (
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
            ) : (
              <FontAwesomeIcon icon={faUsers} className="text-muted" />
            )}
          </div>
        </div>
      )}

      {/* Search results */}
      {!disabled && filteredPersons.length > 0 && (
        <div className="mt-2">
          <Stack gap={0} className="shadow-sm" style={{ maxHeight: '240px', overflowY: 'auto' }}>
            {filteredPersons.map(person => (
              <div 
                key={person._id}
                onClick={() => handlePersonSelect(person)}
                className="d-flex align-items-center py-3 border-0 border-bottom"
                style={{ cursor: 'pointer', padding: '0.75rem' }}
              >
                <div className="flex-shrink-0 me-3">
                  <FontAwesomeIcon icon={faUserCircle} size="lg" className="text-muted" />
                </div>
                <div className="flex-grow-1">
                  <div className="fw-semibold">{person.fullName}</div>
                  <div className="text-muted small">ID: {person.externalIdentifier}</div>
                </div>
                <div className="flex-shrink-0">
                  <FontAwesomeIcon icon={faPlus} className="text-primary" />
                </div>
              </div>
            ))}
          </Stack>
          <div className="small text-muted mt-1">
            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
            {filteredPersons.length} result{filteredPersons.length !== 1 ? 's' : ''} found
          </div>
        </div>
      )}

      {loading && !searchTerm && (
        <div className="text-center text-muted small mt-3 py-2">
          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
          Loading persons...
        </div>
      )}
      
      {/* Empty state for search */}
      {!disabled && searchTerm && filteredPersons.length === 0 && !loading && (
        <div className="text-center text-muted small mt-3 py-3">
          <FontAwesomeIcon icon={faSearch} className="me-2" />
          No people found matching "{searchTerm}"
        </div>
      )}
    </Stack>
  );
};

export default PersonMultiSelect;
